import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, type OnChanges, type OnInit, type SimpleChanges } from '@angular/core';
import { BasisCostCenterService } from '@zaoblako/web/core/services/basis/basis-cost-center.service';
import { GlobalService } from '@zaoblako/web/core/services/global.service';
import { MultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/multi-select/multi-select.component';
import { StandardMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/standard-dropdown/standard-multi-select/standard-multi-select.component';
import { reconcileSelectionByNumericIds } from '@zaoblako/web/shared/helpers/dropdown-catalog-selection-reconcile.helper';
import type { FiltersUiVersion } from '@zaoblako/web/core/types/filters-ui-version.type';
import type { CostCenterFindInterface } from '@zaoblako/shared/modules/catalog/dtos/cost-center/cost-center-find.dto';

/**
 * @deprecated Use `crm-standard-multi-select` and `BasisCostCenterService.dropdownDescriptorApi`.
 */
@Component({
  selector: 'crm-cost-center-multi-select',
  standalone: true,
  imports: [MultiSelectComponent, StandardMultiSelectComponent],
  templateUrl: './cost-center-multi-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CostCenterMultiSelectComponent implements OnInit, OnChanges {
  @Input() public filtersUiVersion: FiltersUiVersion = 'v2';
  @Input() public costCenters: CostCenterFindInterface[] = [];
  @Input() public selectedCostCenters: CostCenterFindInterface[] = [];
  @Output() public selectedCostCentersChange = new EventEmitter<CostCenterFindInterface[]>();
  @Input() public customSearch = false;
  @Input() public class: string;
  @Input() public styleClass: string;
  @Input() public showClear = true;
  @Input() public disabled = false;
  @Input() public isError = false;
  @Input() public label: string;
  @Input() public placeholder = '\u00A0';
  @Input() public appendTo: string;
  @Input() public itemStyleClass: string;

  /** Set only after `setSelectedCostCenterIds`. `null` means the filter input is not used. */
  public costCenterIds: number[] | null = null;
  public loading = false;
  public selectionLoading = false;
  public useStandard = false;
  public readonly descriptorUrl: string;
  private inputsReady = false;

  public constructor(
    basisCostCenterService: BasisCostCenterService,
    private readonly globalService: GlobalService
  ) {
    this.descriptorUrl = basisCostCenterService.dropdownDescriptorApi;
  }

  @Input() public set setSelectedCostCenterIds(ids: number[] | null | undefined) {
    this.costCenterIds = (ids ?? []).map((id) => (typeof id === 'string' ? Number(id) : id));
    if (this.inputsReady && !this.useStandard) {
      this.applyCustomSelection();
    }
  }

  public ngOnInit(): void {
    this.useStandard = !this.customSearch && !!this.globalService.globalInfo?.tenantInfo?.ratePlan?.modulesAccessControl?.BASIS?.enabled;
    this.inputsReady = true;
    if (!this.useStandard) {
      this.applyCustomSelection();
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (!this.useStandard && changes.costCenters && !changes.costCenters.isFirstChange()) {
      this.applyCustomSelection();
    }
  }

  public onSelected = (costCenters: CostCenterFindInterface[]): void => {
    this.selectedCostCenters = costCenters ?? [];
    this.selectedCostCentersChange.emit(this.selectedCostCenters);
  };

  private applyCustomSelection = (): void => {
    const ids = this.costCenterIds;
    if (!ids?.length) {
      this.selectionLoading = false;
      return;
    }

    const { selected, hasStaleKeys } = reconcileSelectionByNumericIds(this.costCenters, ids);
    const selectionDiffers =
      !this.selectedCostCenters?.length ||
      this.selectedCostCenters.length !== selected.length ||
      !this.selectedCostCenters.every((costCenter) => selected.some((item) => item.id === costCenter.id));

    if (!selectionDiffers && !hasStaleKeys) {
      this.selectionLoading = false;
      return;
    }

    this.selectedCostCenters = selected;
    this.selectionLoading = false;
    if (hasStaleKeys) {
      this.onSelected(selected);
    }
  };
}
